// Open/close channel for the in-app feedback flow (#1998, entry-intent added #2204).
//
// The entry points (Header user menu, Sidebar drawer item, Cmd/Ctrl+Shift+F) live in different
// component trees from the host that renders the modal + annotate overlay (`FeedbackHost`, mounted
// once in AppShell). This store is the seam between them — deliberately tiny: it carries "is the
// flow open", which intent launched it, and a `sessionId` bumped only on a genuine entry-point
// click, nothing about the report itself, which stays local to the host.

import { create } from 'zustand'

/** Which action the user picked at the entry point — drives what FeedbackHost opens directly into. */
export type FeedbackIntent = 'problem' | 'feature' | null

interface FeedbackState {
  isOpen: boolean
  intent: FeedbackIntent
  /**
   * Increments on every `openFeedback()` call — unlike `isOpen`, it does NOT change when the host
   * closes the modal internally to run the annotate overlay and reopens it on capture. FeedbackModal
   * uses this (not the `open` prop) to decide when a genuinely fresh entry-point click should reset
   * the report/preselected type, so that internal round-trip doesn't wipe a type or description the
   * user already picked (#2204 regression).
   */
  sessionId: number
  /** "My reports" (#2548) — a separate read-only modal, so it gets its own open flag rather than
   * overloading `intent` (which drives what the SUBMISSION modal opens into). */
  isMyReportsOpen: boolean
}

interface FeedbackActions {
  openFeedback: (intent?: FeedbackIntent) => void
  closeFeedback: () => void
  openMyReports: () => void
  closeMyReports: () => void
}

export const useFeedbackStore = create<FeedbackState & FeedbackActions>((set) => ({
  isOpen: false,
  intent: null,
  sessionId: 0,
  isMyReportsOpen: false,
  openFeedback: (intent = null) =>
    set((state) => ({ isOpen: true, intent, sessionId: state.sessionId + 1 })),
  closeFeedback: () => set({ isOpen: false, intent: null }),
  openMyReports: () => set({ isMyReportsOpen: true }),
  closeMyReports: () => set({ isMyReportsOpen: false }),
}))

export const selectIsFeedbackOpen = (state: FeedbackState): boolean => state.isOpen
export const selectFeedbackIntent = (state: FeedbackState): FeedbackIntent => state.intent
export const selectFeedbackSessionId = (state: FeedbackState): number => state.sessionId
export const selectIsMyReportsOpen = (state: FeedbackState): boolean => state.isMyReportsOpen
